import { useActivityDiffs } from "@/hooks/useActivityDiffs";
import ActivityStream from "./ActivityStream";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useLayoutStore from "@/store/zustand/layoutStore";
import { EditorContext, useCodebaseStore } from "@/store/zustand/codebaseStore";
import { RedoIcon, UndoIcon } from "lucide-react";
import { Button } from "../ui/button";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import useActivityStore from "@/store/zustand/activityStore";

const HistoryPanel = () => {
  const allEditorValues = useSelector((state: any) => state.editor);
  const editorFiles = allEditorValues?.[EditorContext.History]?.editorFiles;
  const openHistoryFiles = useCodebaseStore((state) => state.openHistoryFiles);
  const clearHistoryFiles = useCodebaseStore(
    (state) => state.clearHistoryFiles,
  );

  const activityStreamList = useActivityStore(
    (state) => state.activityStreamList,
  );

  const [selectedActivityStreamId, setSelectedActivityStreamId] = useState<
    string | null
  >(null);

  const expandLeft = useLayoutStore((state) => state.expandLeft);

  const redo = useDesignCommandManager((state) => state.redo);
  const undo = useDesignCommandManager((state) => state.undo);

  const { calculatedDiffs, calculatedDiffsActivityId, diffErrors } =
    // @ts-expect-error TS2345
    useActivityDiffs(selectedActivityStreamId);

  useEffect(() => {
    if (selectedActivityStreamId !== calculatedDiffsActivityId) {
      // Activity diffs not loaded yet, wait until they are
      return;
    }

    if (calculatedDiffs.length && !editorFiles?.length) {
      setSelectedActivityStreamId(null);
    }
  }, [editorFiles]);

  useEffect(() => {
    if (activityStreamList?.length) {
      const lastNotUndone = activityStreamList?.find(
        (activity) => !activity.change_undone,
      );
      if (!lastNotUndone) {
        setSelectedActivityStreamId(
          activityStreamList?.[activityStreamList.length - 1]?.id,
        );
      } else {
        setSelectedActivityStreamId(lastNotUndone.id);
      }
    }
  }, [activityStreamList]);

  useEffect(() => {
    if (selectedActivityStreamId !== calculatedDiffsActivityId) {
      // Activity diffs not loaded yet, wait until they are
      return;
    }

    if (selectedActivityStreamId && calculatedDiffs.length) {
      expandLeft();

      // Open the files after a slight delay to ensure the code panel is mounted
      // Solves a race condition
      setTimeout(() => {
        openHistoryFiles({
          calculatedDiffs,
          activityId: selectedActivityStreamId,
        });
      }, 100);
    }

    if (!selectedActivityStreamId || !calculatedDiffs.length) {
      clearHistoryFiles();
    }
  }, [selectedActivityStreamId, calculatedDiffs]);

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex flex-row items-center justify-between border-b border-secondary-300 ">
        <div className="p-2 text-xs text-secondary-900 font-bold">History</div>
        <div className="flex flex-row items-center flex-0 p-1">
          <Button
            variant="ghost"
            size="xs"
            className="py-1 px-1.5"
            data-tooltip-id="tooltip"
            data-tooltip-content="Undo"
            data-tooltip-place="bottom"
            onClick={() => {
              undo();
            }}
          >
            <UndoIcon className="w-3.5 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="xs"
            className="py-1 px-1.5"
            data-tooltip-id="tooltip"
            data-tooltip-content="Redo"
            data-tooltip-place="bottom"
            onClick={() => {
              redo();
            }}
          >
            <RedoIcon className="w-3.5 h-4" />
          </Button>
        </div>
      </div>
      <ActivityStream
        selectActivity={(activity) => {
          if (activity) {
            setSelectedActivityStreamId(activity.id);
          } else {
            setSelectedActivityStreamId(null);
          }
        }}
        // @ts-expect-error TS2322
        selectedActivityId={selectedActivityStreamId}
      />
    </div>
  );
};

export default HistoryPanel;
